import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { betweenBound, dateQueryPreview, needsDatePreview } from "@/lib/date-query-preview";
import { cn, getRelativeDateExamples } from "@/lib/utils";
import { DataService } from "@/services/data";
import { useDebounce } from "@/utils/misc";
import { fetchSearchLight } from "@/utils/search-light";
import { CalendarDays, Check, ChevronDown, Loader, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { FormulaContext } from "./formula/api";
import { GeoValueInput } from "./geo-input";
import { VALUELESS, containsFilter, geoValueOf } from "./tree-utils";
import type { FilterOperator, SchemaField } from "./types";

type Values = unknown[];

export type ValueInputProps = {
    field: SchemaField;
    operator: FilterOperator;
    value: Values;
    onChange: (value: Values, labels?: Record<string, string>) => void;
    /** Target model of a `record` field, to search its records. */
    recordModel?: string;
    /** The object the condition filters and the path of the field: a `geo` field measures from a record reached from it. */
    rootModel?: string;
    fieldPath?: string;
    /** The records around the condition: a distance can be measured from one of their locations. */
    scope?: FormulaContext;
    valueLabels?: Record<string, string> | null;
    invalid?: boolean;
};

/** The value control of a condition, by field type and operator. Values are always a list. */
export function ValueInput(props: ValueInputProps) {
    const { field, operator, value, onChange, invalid } = props;
    if (VALUELESS.has(operator)) return null;

    if (operator === "between") {
        const [low = "", high = ""] = value.map(item => String(item ?? ""));
        if (field.type === "number") {
            return (
                <div className="flex items-center gap-2">
                    <NumberInput value={low} onChange={next => onChange([next, high])} invalid={invalid && !low} placeholder="From" />
                    <span className="text-xs text-gray-500">and</span>
                    <NumberInput value={high} onChange={next => onChange([low, next])} invalid={invalid && !high} placeholder="To" />
                </div>
            );
        }
        return (
            <div className="flex items-center gap-2">
                <DateInput value={low} onChange={next => onChange([next, high])} invalid={invalid && !low} placeholder="From" resolved={betweenBound(low, true)} compact />
                <span className="text-xs text-gray-500">and</span>
                <DateInput value={high} onChange={next => onChange([low, next])} invalid={invalid && !high} placeholder="To" resolved={betweenBound(high, false)} compact />
            </div>
        );
    }

    switch (field.type) {
        case "geo":
            return <GeoValueInput value={geoValueOf(value)} onChange={next => onChange([next])} rootModel={props.rootModel} fieldPath={props.fieldPath} scope={props.scope} invalid={invalid} />;
        case "choice":
        case "picklist":
            return <OptionsSelect field={field} value={value.map(String)} onChange={onChange} invalid={invalid} />;
        case "record":
            return <RecordSelect model={props.recordModel} value={value.map(String)} labels={props.valueLabels ?? {}} onChange={onChange} invalid={invalid} />;
        case "date":
        case "datetime": {
            const typed = String(value[0] ?? "");
            return <DateInput value={typed} onChange={next => onChange(next ? [next] : [])} invalid={invalid} withTime={field.type === "datetime"} resolved={resolvedDate(operator, [typed])} />;
        }
        case "number":
            if (operator === "equals" || operator === "not_equals") {
                return <TagInput value={value.map(String)} onChange={onChange} invalid={invalid} placeholder="Type a number, Enter to add" numeric />;
            }
            return <NumberInput value={String(value[0] ?? "")} onChange={next => onChange(next ? [next] : [])} invalid={invalid} />;
        default:
            return <TagInput value={value.map(String)} onChange={onChange} invalid={invalid} placeholder="Type a value, Enter to add" />;
    }
}

const controlClass = "h-8 rounded-sm bg-white text-sm shadow-sm";

function NumberInput({ value, onChange, invalid, placeholder = "Number" }: { value: string; onChange: (value: string) => void; invalid?: boolean; placeholder?: string }) {
    return (
        <Input
            type="number"
            inputMode="decimal"
            // The input's box takes its own width: in a condition's row, the rest stays on the line.
            containerClassName="w-32"
            className={cn(controlClass, "w-32 tabular-nums", invalid && "border-red-500")}
            value={value}
            placeholder={placeholder}
            onChange={event => onChange(event.target.value)}
        />
    );
}

/** Several free values (OR for positive operators, all excluded for negative ones). */
function TagInput({ value, onChange, invalid, placeholder, numeric }: { value: string[]; onChange: (value: Values) => void; invalid?: boolean; placeholder: string; numeric?: boolean }) {
    const [draft, setDraft] = useState("");
    const commit = (raw = draft) => {
        const parts = raw
            .split(numeric ? /[;\s]+/ : /;/)
            .map(part => part.trim())
            .filter(Boolean)
            .filter(part => !value.includes(part));
        if (parts.length) onChange([...value, ...parts]);
        setDraft("");
    };
    return (
        <div
            className={cn(
                "flex min-h-8 min-w-[220px] max-w-[420px] flex-1 flex-wrap items-center gap-1 rounded-sm border border-input bg-white px-1.5 py-1 shadow-sm focus-within:ring-1 focus-within:ring-ring",
                invalid && "border-red-500"
            )}
        >
            {value.map(item => (
                <Badge key={item} tone="neutral" className="gap-1 px-1.5">
                    {item}
                    <button type="button" aria-label={`Remove ${item}`} onClick={() => onChange(value.filter(other => other !== item))}>
                        <X className="h-3 w-3" />
                    </button>
                </Badge>
            ))}
            <input
                className="min-w-[80px] flex-1 bg-transparent text-sm outline-none placeholder:text-gray-500"
                value={draft}
                inputMode={numeric ? "decimal" : undefined}
                placeholder={value.length ? "" : placeholder}
                onChange={event => setDraft(event.target.value)}
                onBlur={() => commit()}
                onKeyDown={event => {
                    if (event.key === "Enter" || event.key === "Tab" || event.key === ";") {
                        if (draft.trim()) {
                            event.preventDefault();
                            commit();
                        }
                    } else if (event.key === "Backspace" && !draft && value.length) {
                        onChange(value.slice(0, -1));
                    }
                }}
                onPaste={event => {
                    const text = event.clipboardData.getData("text");
                    if (/[\n;]/.test(text)) {
                        event.preventDefault();
                        commit(text.replace(/\n/g, ";"));
                    }
                }}
            />
        </div>
    );
}

function MultiSelectTrigger({ labels, placeholder, invalid, loading }: { labels: string[]; placeholder: string; invalid?: boolean; loading?: boolean }) {
    return (
        <Button type="button" variant="outline" className={cn(controlClass, "min-w-[220px] max-w-[420px] justify-between px-2 font-normal", invalid && "border-red-500")}>
            <span className={cn("truncate", !labels.length && "text-gray-500")}>{labels.length ? labels.join(", ") : placeholder}</span>
            {loading ? <Loader className="ml-2 h-4 w-4 animate-spin text-gray-500" /> : <ChevronDown className="ml-2 h-4 w-4 flex-shrink-0 text-gray-500" />}
        </Button>
    );
}

type Option = { value: string; label: string };

function normalizeOptions(data: unknown): Option[] {
    const rows = Array.isArray(data) ? data : ((data as { results?: unknown[] })?.results ?? []);
    return (rows as Array<Record<string, unknown>>).map(row => ({
        value: String(row.value ?? row.id ?? ""),
        label: String(row.display_name ?? row.label ?? row.name ?? row.value ?? "")
    }));
}

function OptionsSelect({ field, value, onChange, invalid }: { field: SchemaField; value: string[]; onChange: (value: Values) => void; invalid?: boolean }) {
    const [open, setOpen] = useState(false);
    const [remote, setRemote] = useState<Option[] | null>(null);
    const [loading, setLoading] = useState(false);
    useEffect(() => {
        if (!field.options_url || remote !== null || (!open && value.length === 0)) return;
        setLoading(true);
        DataService.client
            .url(field.options_url)
            .get()
            .json()
            .then(data => setRemote(normalizeOptions(data)))
            .catch(() => setRemote([]))
            .finally(() => setLoading(false));
    }, [field.options_url, open, remote, value.length]);
    const options = field.options ?? remote ?? [];
    const labelOf = (item: string) => options.find(option => option.value === item)?.label ?? item;
    const toggle = (item: string) => onChange(value.includes(item) ? value.filter(other => other !== item) : [...value, item]);
    return (
        <Popover open={open} onOpenChange={setOpen} modal>
            <PopoverTrigger asChild>
                <span>
                    <MultiSelectTrigger labels={value.map(labelOf)} placeholder="Select values" invalid={invalid} loading={loading} />
                </span>
            </PopoverTrigger>
            <PopoverContent className="flex max-h-[var(--radix-popover-content-available-height)] w-[320px] flex-col p-0" align="start" collisionPadding={12}>
                <Command filter={containsFilter} className="min-h-0 flex-1">
                    <CommandInput placeholder="Search values..." />
                    <CommandList className="max-h-[300px] min-h-0 flex-1">
                        <CommandEmpty>{loading ? "Loading..." : "No value found"}</CommandEmpty>
                        <CommandGroup>
                            {options.map(option => (
                                <CommandItem key={option.value} value={`${option.label} ${option.value}`} onSelect={() => toggle(option.value)}>
                                    <Check className={cn("mr-2 h-4 w-4", value.includes(option.value) ? "opacity-100" : "opacity-0")} />
                                    {option.label}
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}

function RecordSelect({
    model,
    value,
    labels,
    onChange,
    invalid
}: {
    model?: string;
    value: string[];
    labels: Record<string, string>;
    onChange: (value: Values, labels?: Record<string, string>) => void;
    invalid?: boolean;
}) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const debounced = useDebounce(query, 300);
    const [results, setResults] = useState<Option[]>([]);
    const [loading, setLoading] = useState(false);
    useEffect(() => {
        if (!model || debounced.trim().length < 2) {
            setResults([]);
            return;
        }
        const controller = new AbortController();
        setLoading(true);
        fetchSearchLight(debounced, model, controller)
            .then((data: Record<string, { results?: Array<{ id: number; title?: string }> }> | null) => {
                const rows = data?.[model]?.results ?? [];
                setResults(rows.map(row => ({ value: String(row.id), label: row.title || `#${row.id}` })));
            })
            .catch(() => setResults([]))
            .finally(() => setLoading(false));
        return () => controller.abort();
    }, [debounced, model]);
    const toggle = (option: Option) => {
        if (value.includes(option.value)) {
            onChange(
                value.filter(item => item !== option.value),
                labels
            );
        } else {
            onChange([...value, option.value], { ...labels, [option.value]: option.label });
        }
    };
    const shown = useMemo(() => value.map(item => labels[item] ?? `#${item}`), [value, labels]);
    return (
        <Popover open={open} onOpenChange={setOpen} modal>
            <PopoverTrigger asChild>
                <span>
                    <MultiSelectTrigger labels={shown} placeholder="Search records" invalid={invalid} />
                </span>
            </PopoverTrigger>
            <PopoverContent className="flex max-h-[var(--radix-popover-content-available-height)] w-[340px] flex-col p-0" align="start" collisionPadding={12}>
                <Command shouldFilter={false} className="min-h-0 flex-1">
                    <CommandInput placeholder="Type at least 2 characters..." value={query} onValueChange={setQuery} />
                    <CommandList className="max-h-[300px] min-h-0 flex-1">
                        {value.length > 0 && (
                            <CommandGroup heading="Selected">
                                {value.map(item => (
                                    <CommandItem key={`selected-${item}`} value={`selected-${item}`} onSelect={() => toggle({ value: item, label: labels[item] ?? item })}>
                                        <Check className="mr-2 h-4 w-4" />
                                        {labels[item] ?? `#${item}`}
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        )}
                        {loading && (
                            <div className="flex items-center gap-2 p-3 text-sm text-gray-500">
                                <Loader className="h-4 w-4 animate-spin" /> Loading...
                            </div>
                        )}
                        {!loading && debounced.trim().length >= 2 && results.length === 0 && <div className="p-3 text-sm text-gray-500">No result found</div>}
                        {results.length > 0 && (
                            <CommandGroup heading="Results">
                                {results
                                    .filter(option => !value.includes(option.value))
                                    .map(option => (
                                        <CommandItem key={option.value} value={option.value} onSelect={() => toggle(option)}>
                                            <Check className="mr-2 h-4 w-4 opacity-0" />
                                            {option.label}
                                        </CommandItem>
                                    ))}
                            </CommandGroup>
                        )}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}

const RELATIVE_PRESETS = getRelativeDateExamples();

function resolvedDate(operator: string, values: string[]): string | null {
    return needsDatePreview(values) ? dateQueryPreview(operator, values) : null;
}

/**
 * A date as the list filters understand it: a relative period (« last 7 days », « this month »)
 * kept as typed so a saved view stays relative, or a fixed date (« 31/01/2026 », « 2026-01-31 »).
 */
function DateInput({
    value,
    onChange,
    invalid,
    placeholder = "today, last 7 days, 31/01/2026…",
    withTime,
    resolved,
    compact
}: {
    value: string;
    onChange: (value: string) => void;
    invalid?: boolean;
    placeholder?: string;
    withTime?: boolean;
    /** The dates the expression means right now, shown inside the right edge of the input. */
    resolved?: string | null;
    /** One bound of a between: it resolves to a single date, so two inputs fit on the row. */
    compact?: boolean;
}) {
    const [open, setOpen] = useState(false);
    return (
        <div className="flex items-center">
            <div className="relative">
                <Input
                    className={cn(controlClass, compact ? "w-52" : "w-80", "rounded-r-none", resolved && (compact ? "pr-24" : "pr-44"), invalid && "border-red-500")}
                    value={value}
                    placeholder={placeholder}
                    onChange={event => onChange(event.target.value)}
                />
                {resolved && (
                    <span
                        className={cn("pointer-events-none absolute inset-y-0 right-2 flex items-center truncate text-xs tabular-nums text-muted-foreground", compact ? "max-w-20" : "max-w-40")}
                        title={resolved}
                    >
                        {resolved}
                    </span>
                )}
            </div>
            <Popover open={open} onOpenChange={setOpen} modal>
                <PopoverTrigger asChild>
                    <Button type="button" variant="outline" size="icon" className="h-8 w-8 rounded-sm rounded-l-none border-l-0 bg-white shadow-sm" aria-label="Pick a date">
                        <CalendarDays className="h-4 w-4" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[280px] p-2" align="end">
                    <p className="mb-1 text-xs text-gray-600">A fixed date</p>
                    <Input
                        type={withTime ? "datetime-local" : "date"}
                        className={controlClass}
                        onChange={event => {
                            if (!event.target.value) return;
                            onChange(withTime ? event.target.value.replace("T", " ") : event.target.value);
                            setOpen(false);
                        }}
                    />
                    <p className="mb-1 mt-3 text-xs text-gray-600">Relative to today</p>
                    <div className="flex flex-wrap gap-1">
                        {RELATIVE_PRESETS.map(preset => (
                            <button
                                key={preset}
                                type="button"
                                className={cn("rounded-sm border px-1.5 py-0.5 text-xs hover:bg-gray-100", preset === value && "border-primary bg-gray-100")}
                                onClick={() => {
                                    onChange(preset);
                                    setOpen(false);
                                }}
                            >
                                {preset}
                            </button>
                        ))}
                    </div>
                </PopoverContent>
            </Popover>
        </div>
    );
}
